/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import { createSelector } from '@reduxjs/toolkit';
import { getRootOrg } from './initiative';
import { UserRoles } from '../constants/user';
import { CurrentUserData, CurrentUserState } from '../reducers/current-user';
import { InitiativePermissions } from '../services/permissions/InitiativePermissions';
import { PortfolioPermissions } from '@services/permissions/PortfolioPermissions';
import { InitiativeState } from '../reducers/initiative';

interface State {
  currentUser: CurrentUserState;
  initiative: InitiativeState;
}

export const getCurrentUser = (state: State): CurrentUserData | undefined => {
  return state.currentUser.loaded === true ? state.currentUser.data : undefined;
};

export const isUserLoaded = (state: State) => state.currentUser.loaded;

export const isUserActivated = (state: State) => {
  const isActive = state.currentUser.data.active;
  const hasInitiatives = Boolean(state.currentUser.data.permissions?.length ?? false);
  return isActive && hasInitiatives;
}

export const isStaff = createSelector(
  [getCurrentUser],
  (user) => user ? user.isStaff : false
)

export const isStaffSuperUser = createSelector([getCurrentUser], (user) => (user ? user.isSuperUser : false));

export const canAccessCurrentLevel = (state: State) => {
  if (!state.currentUser.loaded || !state.initiative.loaded) {
    return false;
  }
  return InitiativePermissions.canAccess(state.currentUser.data, state.initiative.data?._id);
}
export const canManageCurrentLevel = (state: State) => {
  if (!state.currentUser.loaded || !state.initiative.loaded) {
    return false;
  }
  return InitiativePermissions.canManageInitiative(state.currentUser.data, state.initiative.data?._id);
}
/**
 * @deprecated use canManageCurrentLevel instead
 */
export const isUserManager = canManageCurrentLevel;

export const canAccessInsightsAndDownloads = (state: State, initiativeId?: string) => {
  if (!state.currentUser.loaded || !initiativeId) {
    return false;
  }
  return InitiativePermissions.canAccessInsightsAndDownloads(state.currentUser.data, initiativeId);
}

export const canCreateSurvey = (state: State) => canManageCurrentLevel(state);

export const isUserManagerByInitiativeId = (state: State, initiativeId?: string) => {
  if (!state.currentUser.loaded || !initiativeId) {
    return false;
  }
  const user = state.currentUser.data;
  return InitiativePermissions.canManageInitiative(user, initiativeId);
}

export const isOrgOwner = createSelector([
  getCurrentUser,
  getRootOrg,
], (user, org) => {
  if (!user || !user.permissions || !org) {
    return false;
  }

  return user.permissions.some((p) => {
    return p.initiativeId === org._id && p.permissions.includes(UserRoles.Owner);
  })
})

export const isOrgManager = createSelector([getCurrentUser, getRootOrg], (user, org) => {
  if (!user || !user.permissions || !org) {
    return false;
  }

  return user.permissions.some((p) => {
    return p.initiativeId === org._id && p.permissions.some(perm => [UserRoles.Manager, UserRoles.Owner].includes(perm));
  });
});

export const checkCanAccessRootOrg = createSelector([getCurrentUser, getRootOrg], (user, org) => {
  if (!user || !org) {
    return false;
  }

  return InitiativePermissions.canAccess(user, org._id);
});

export const isUserManagerByPortfolioId = (state: State, portfolioId?: string) => {
  if (!state.currentUser.loaded || !portfolioId) {
    return false;
  }
  const user = state.currentUser.data;
  return PortfolioPermissions.canManagePortfolio(user, portfolioId);
};
